/**
 * CDN Health Monitor
 * Provides real-time CDN health status, logging, and alert management
 */

import {
  getCdnStatus,
  getCdnStatusMessage,
  resetCdnStatus,
  resolveCdnReportingEndpoint,
  setCdnOffline,
} from './cdnFallback';

interface CdnAlert {
  id: string;
  timestamp: Date;
  type: 'error' | 'warning' | 'recovery';
  message: string;
  severity: 'low' | 'medium' | 'high';
  details?: Record<string, any>;
}

interface CdnMonitorConfig {
  alertEmail?: string;
  enableConsoleLogging?: boolean;
  enableLocalStorage?: boolean;
  maxLogsToKeep?: number;
}

class CdnHealthMonitor {
  private alerts: CdnAlert[] = [];
  private config: CdnMonitorConfig;
  private alertCheckInterval: number | null = null;
  private lastAlertSent: number = 0;
  private alertDebounce: number = 60000; // 1 minute minimum between alerts

  constructor(config: CdnMonitorConfig = {}) {
    this.config = {
      alertEmail: config.alertEmail,
      enableConsoleLogging: config.enableConsoleLogging !== false,
      enableLocalStorage: config.enableLocalStorage !== false,
      maxLogsToKeep: config.maxLogsToKeep || 100,
    };

    this.loadAlertsFromStorage();
    this.startHealthCheck();
    this.exposeToWindow();
  }

  /**
   * Expose monitor to window for console access
   */
  private exposeToWindow(): void {
    (window as any).__SC_CDN_MONITOR__ = {
      status: () => this.getStatus(),
      alerts: () => this.getAlerts(),
      clarity: () => this.printClarity(),
      reset: () => this.resetStatus(),
      forceOffline: () => this.setCdnOffline(true),
      forceOnline: () => this.setCdnOffline(false),
      testCdn: (url: string) => this.testCdnUrl(url),
      sendAlert: (msg: string) => this.sendAlert(msg, 'manual'),
      config: () => this.config,
    };

    console.log(
      '%c[CDN MONITOR] Run window.__SC_CDN_MONITOR__.clarity() for status',
      'color: #0066cc; font-weight: bold'
    );
  }

  /**
   * Print human-readable status to console
   */
  printClarity(): void {
    const status = getCdnStatus();
    console.clear();
    console.log(
      '%c=== STORYCORE CDN STATUS ===',
      'color: #0066cc; font-weight: bold; font-size: 16px'
    );
    console.log(getCdnStatusMessage());
    console.log('');
    console.log(`%cFailure Count: %c${status.failureCount}`, 'font-weight: bold', 'color: #d32f2f');
    console.log(
      `%cTime Since Last: %c${this.timeSinceLastFailure()}`,
      'font-weight: bold',
      'color: #ff9800'
    );
    console.log(
      `%cAlerts: %c${this.alerts.length}`,
      'font-weight: bold',
      this.alerts.length > 0 ? 'color: #d32f2f' : 'color: #4caf50'
    );
    console.log('');

    if (this.alerts.length > 0) {
      console.log('%cRecent Alerts:', 'font-weight: bold; color: #ff9800');
      this.alerts.slice(-5).forEach((alert) => {
        const color = alert.severity === 'high' ? '#d32f2f' : '#ff9800';
        console.log(
          `  %c[${alert.type.toUpperCase()}] %c${alert.message}`,
          `color: ${color}`,
          'color: #666'
        );
      });
    }

    console.log('');
    console.log('%cAvailable Commands:', 'font-weight: bold; color: #0066cc');
    console.log('  window.__SC_CDN_MONITOR__.status()     - Full status object');
    console.log('  window.__SC_CDN_MONITOR__.reset()      - Reset CDN status');
    console.log('  window.__SC_CDN_MONITOR__.forceOffline() - Simulate CDN failure');
    console.log('  window.__SC_CDN_MONITOR__.forceOnline()  - Force CDN online');
    console.log('  window.__SC_CDN_MONITOR__.testCdn(url)   - Test CDN URL');
    console.log('  window.__SC_CDN_MONITOR__.sendAlert(msg) - Send test alert');
  }

  /**
   * Get current CDN status
   */
  getStatus() {
    const status = getCdnStatus();
    return {
      ...status,
      statusMessage: getCdnStatusMessage(),
      alertCount: this.alerts.length,
      lastAlertTime: this.lastAlertSent ? new Date(this.lastAlertSent) : null,
    };
  }

  /**
   * Get all stored alerts
   */
  getAlerts(): CdnAlert[] {
    return [...this.alerts];
  }

  /**
   * Add an alert
   */
  private addAlert(alert: CdnAlert): void {
    this.alerts.push(alert);

    // Keep only recent alerts
    if (this.alerts.length > (this.config.maxLogsToKeep || 100)) {
      this.alerts = this.alerts.slice(-this.config.maxLogsToKeep!);
    }

    this.saveAlertsToStorage();

    if (this.config.enableConsoleLogging) {
      const style = `color: ${alert.severity === 'high' ? '#d32f2f' : '#ff9800'}; font-weight: bold`;
      console.log(`%c[CDN ALERT] ${alert.message}`, style);
    }

    // Send email if high severity and debounce passed
    if (alert.severity === 'high' && this.shouldSendAlert()) {
      this.sendToServer(alert);
    }
  }

  /**
   * Check if enough time has passed to send another alert
   */
  private shouldSendAlert(): boolean {
    const now = Date.now();
    if (now - this.lastAlertSent > this.alertDebounce) {
      this.lastAlertSent = now;
      return true;
    }
    return false;
  }

  /**
   * Send alert to server for email notification
   */
  private sendToServer(alert: CdnAlert): void {
    const endpoint = resolveCdnReportingEndpoint("alert");

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...alert,
        timestamp: alert.timestamp.toISOString(),
        userUrl: window.location.href,
        userAgent: navigator.userAgent,
      }),
    }).catch((err) => {
      console.error('[CDN MONITOR] Failed to send alert to server:', err);
    });
  }

  /**
   * Health check - periodically verify CDN status
   */
  private startHealthCheck(): void {
    // Check every 5 minutes
    this.alertCheckInterval = window.setInterval(() => {
      const status = getCdnStatus();

      if (status.isFailing && status.failureCount % 3 === 1) {
        // Alert every 3rd failure (after first failure)
        this.addAlert({
          id: `alert-${Date.now()}`,
          timestamp: new Date(),
          type: 'error',
          message: `CDN failing: ${status.lastError?.message || 'Unknown error'}`,
          severity: 'high',
          details: {
            failureCount: status.failureCount,
            lastError: status.lastError,
          },
        });
      }
    }, 60000); // Check every minute
  }

  /**
   * Stop health monitoring
   */
  stopHealthCheck(): void {
    if (this.alertCheckInterval !== null) {
      clearInterval(this.alertCheckInterval);
      this.alertCheckInterval = null;
    }
  }

  /**
   * Reset CDN status
   */
  resetStatus(): void {
    resetCdnStatus();
    this.addAlert({
      id: `alert-${Date.now()}`,
      timestamp: new Date(),
      type: 'recovery',
      message: 'CDN status reset - monitoring resumed',
      severity: 'low',
    });
  }

  /**
   * Force CDN online/offline
   */
  setCdnOffline(offline: boolean): void {
    setCdnOffline(offline);
    this.addAlert({
      id: `alert-${Date.now()}`,
      timestamp: new Date(),
      type: offline ? 'error' : 'recovery',
      message: `CDN forced ${offline ? 'OFFLINE' : 'ONLINE'} for testing`,
      severity: offline ? 'high' : 'low',
    });
  }

  /**
   * Test a CDN URL
   */
  async testCdnUrl(url: string): Promise<boolean> {
    try {
      const response = await Promise.race([
        fetch(url, { method: 'HEAD' }),
        new Promise<Response>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 5000)
        ),
      ]);

      const success = response.ok;
      this.addAlert({
        id: `alert-${Date.now()}`,
        timestamp: new Date(),
        type: success ? 'recovery' : 'error',
        message: `CDN test ${success ? 'SUCCESS' : `FAILED (${response.status})`}: ${url}`,
        severity: success ? 'low' : 'medium',
      });

      return success;
    } catch (err) {
      this.addAlert({
        id: `alert-${Date.now()}`,
        timestamp: new Date(),
        type: 'error',
        message: `CDN test error: ${err instanceof Error ? err.message : String(err)}`,
        severity: 'medium',
      });

      return false;
    }
  }

  /**
   * Send custom alert (for testing)
   */
  sendAlert(message: string, source: string = 'custom'): void {
    const alert: CdnAlert = {
      id: `alert-${Date.now()}`,
      timestamp: new Date(),
      type: 'warning',
      message: `${message} (from: ${source})`,
      severity: 'medium',
    };

    this.addAlert(alert);
    this.sendToServer(alert);
  }

  /**
   * Time since last failure
   */
  private timeSinceLastFailure(): string {
    const status = getCdnStatus();
    if (!status.isFailing && status.lastFailure === 0) return 'Never';

    const ms = Date.now() - status.lastFailure;
    if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
    return `${Math.floor(ms / 3600000)}h ago`;
  }

  /**
   * Save alerts to localStorage
   */
  private saveAlertsToStorage(): void {
    if (!this.config.enableLocalStorage) return;

    try {
      const serializable = this.alerts.map((a) => ({
        ...a,
        timestamp: a.timestamp.toISOString(),
      }));

      localStorage.setItem('__sc_cdn_alerts__', JSON.stringify(serializable));
    } catch (err) {
      console.warn('[CDN MONITOR] Failed to save alerts to storage:', err);
    }
  }

  /**
   * Load alerts from localStorage
   */
  private loadAlertsFromStorage(): void {
    if (!this.config.enableLocalStorage) return;

    try {
      const stored = localStorage.getItem('__sc_cdn_alerts__');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.alerts = parsed.map(
          (a: any) =>
            ({
              ...a,
              timestamp: new Date(a.timestamp),
            }) as CdnAlert
        );
      }
    } catch (err) {
      console.warn('[CDN MONITOR] Failed to load alerts from storage:', err);
    }
  }
}

// Initialize global monitor
let monitor: CdnHealthMonitor | null = null;

export function initCdnMonitor(config?: CdnMonitorConfig): CdnHealthMonitor {
  if (!monitor) {
    monitor = new CdnHealthMonitor(config);
  }
  return monitor;
}

export function getCdnMonitor(): CdnHealthMonitor | null {
  return monitor;
}

export type { CdnAlert, CdnMonitorConfig };
